import { ref, computed, h } from 'vue'
import { useI18n } from 'vue-i18n'
import { APP_ROUTES } from '@/config/routes'
import * as Icons from 'lucide-vue-next'
import { useMediaQuery } from '@vueuse/core'

// ─── Global State (Singleton Pattern) ───
const STORAGE_KEY = 'bp-sidebar-collapsed'
const initialCollapsed = localStorage.getItem(STORAGE_KEY) === 'true'

const isCollapsed = ref(initialCollapsed)
const isMobileSidebarOpen = ref(false)

/**
 * Composable for Default Layout logic
 */
export function useDefaultLayout() {
  const { t } = useI18n()
  const isMobile = useMediaQuery('(max-width: 1023px)')

  const toggleSidebar = () => {
    isCollapsed.value = !isCollapsed.value
    localStorage.setItem(STORAGE_KEY, String(isCollapsed.value))
  }

  const setCollapsed = (val: boolean) => {
    isCollapsed.value = val
    localStorage.setItem(STORAGE_KEY, String(isCollapsed.value))
  }

  const toggleMobileSidebar = () => {
    isMobileSidebarOpen.value = !isMobileSidebarOpen.value
  }

  const closeMobileSidebar = () => {
    isMobileSidebarOpen.value = false
  }

  const effectiveCollapsed = computed(() => isCollapsed.value)

  const sidebarWidth = computed(() => {
    return isCollapsed.value ? 80 : 256
  })

  /**
   * Helper to convert route structure to menu items
   */
  const buildMenu = (routeObj: any): any[] => {
    return Object.values(routeObj)
      .filter((node: any) => node.meta && !node.meta.hidden)
      .map((node: any) => {
        const Icon = (Icons as any)[node.meta.icon] || Icons.HelpCircle
        const item: any = {
          key: node.name,
          label: t(node.meta.titleKey || ''),
          title: t(node.meta.titleKey || ''),
          icon: () => h(Icon, { size: 17 }),
          to: { name: node.name },
        }

        if (node.children) {
          const visibleChildren = buildMenu(node.children)
          if (visibleChildren.length > 0) {
            item.children = visibleChildren
          }
        }

        return item
      })
  }

  const menuItems = computed(() => buildMenu(APP_ROUTES))

  return {
    menuItems,
    isMobile,
    isMobileSidebarOpen,
    isCollapsed,
    effectiveCollapsed,
    sidebarWidth,
    toggleSidebar,
    setCollapsed,
    toggleMobileSidebar,
    closeMobileSidebar,
  }
}
